/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
 getGridDateOperators,
 getGridNumericOperators,
 getGridStringOperators,
 GridColDef,
 GridRenderCellParams,
 GridValueFormatter,
 GridValueGetter,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';

import { StructureType } from '../../../types/types';
import {
 findStructure,
 getTranslation,
 isJson,
 resolveObjPathDots,
} from '../../../utils/dataTypes';
import xRenderCell from './renderCell';
import { getModelInfo, isWeightModelField } from '@/utils/structureUtils';

const typesMap = {
 int32: 'number',
 int16: 'number',
 boolean: 'boolean',
 double: 'number',
 enum: 'singleSelect',
 decimal: 'number',
 string: 'string',
 datetime: 'dateTime',
 model: 'string',
 array: 'string',
 actions: 'actions',
};

export const renderColObj = (
 col: GridColDef,
 headerName: string,
 structure?: StructureType, // ? because 'actions' has no structure
 renderCell?: (params: GridRenderCellParams<any>) => React.ReactNode,
 valueGetter?: (value: GridValueGetter) => object,
): GridColDef => {
 if (!structure && col.field !== 'actions')
  return {
   field: col.field,
   headerName,
  } as GridColDef;
 //  console.log(structure);

 let colType = structure?.Type;
 let nameVar = 'Name';

 if (colType === 'model') {
  const { name: nameVar } = getModelInfo(
   structure?.ForeignKeyDefaultFilter || '',
  );
  colType =
   structure?.InnerStructure?.find((item) => item.Name === nameVar)?.Type ||
   'string';
 }

 const isWeight = isWeightModelField(structure!);

 const getRightOperators = () => {
  if (isWeight) {
   return getGridNumericOperators().filter((o) =>
    ['=', '!=', '<=', '>='].includes(o.value),
   );
  }
  let output = [];
  switch (structure?.Type) {
   case 'datetime':
    output = getGridDateOperators(true).filter((o) =>
     ['onOrBefore', 'onOrAfter', 'between'].includes(o.value),
    );
    break;
   case 'int32':
   case 'int16':
   case 'double':
   case 'decimal':
    output = getGridNumericOperators().filter((o) =>
     ['=', '!=', '<=', '>='].includes(o.value),
    );
    break;
   case 'enum':
    output = getGridStringOperators().filter((o) =>
     ['equals', 'doesNotEqual', 'contains', 'isAnyOf'].includes(o.value),
    );
    break;
   case 'model':
    output = getGridStringOperators().filter((o) =>
     ['equals', 'doesNotEqual', 'isAnyOf'].includes(o.value),
    );
    break;
   case 'string':
    output = getGridStringOperators().filter((o) =>
     ['equals', 'doesNotEqual', 'contains', 'isAnyOf'].includes(o.value),
    );
    break;
   default:
    output = getGridStringOperators().filter((o) =>
     ['equals', 'doesNotEqual', 'contains'].includes(o.value),
    );
  }
  return output;
 };

 return {
  field: col.field,
  headerName,
  headerClassName: ['model', 'datetime'].includes(structure?.Type || '')
   ? 'model-header'
   : '',
  valueFormatter: (params: GridValueFormatter, row: any) => {
   if (structure?.Type === 'model') {
    return row[col.field]?.[nameVar] || 'wrong name for model';
   }
   return params;
  },
  groupingValueGetter: (params: { [key: string]: any }) =>
   params?.[nameVar] || params, // params need to be value, row
  valueGetter: (value: any, row: any) => {
   const res = resolveObjPathDots(row, col.field);
   const finalVal = value || res;

   if (Array.isArray(finalVal)) {
    return finalVal;
   }
   if (structure?.Type === 'model') {
    if (res && typeof res === 'object' && nameVar in res) {
     return res[nameVar as keyof typeof res];
    }
    return value;
   }
   if (valueGetter) {
    return valueGetter(finalVal);
   }
   if (structure?.Type === 'datetime') {
    if (finalVal === null || finalVal === undefined) {
     return null;
    }

    if (Array.isArray(finalVal)) {
     if (finalVal.length === 0) {
      return [];
     }

     return finalVal.map((item: any) => {
      if (item === null || item === undefined) {
       return null;
      }

      if (typeof item === 'string') {
       return dayjs(item).isValid() ? item : null;
      }

      return dayjs(item).isValid() ? dayjs(item).toDate() : null;
     });
    }

    if (typeof finalVal === 'string') {
     return dayjs(finalVal).isValid() ? finalVal : null;
    }

    return dayjs(finalVal).isValid() ? dayjs(finalVal).toDate() : null;
   }
   return resolveObjPathDots(row, col.field);
  },
  groupable: true,
  aggregable: true,
  type:
   col.field === 'actions'
    ? 'actions'
    : (typesMap as any)?.[colType || 'string'],
  width: col.width || undefined,
  minWidth: col.field === 'actions' ? 56 : 50,
  maxWidth: col.field === 'actions' ? 56 : 300,
  resizable: col.field === 'actions' ? false : true,
  headerAlign: 'left',
  align: 'left',
  sortComparator: (v1, v2, cellParams1, cellParams2) => {
   console.log('sortComparator', v1, v2, cellParams1, cellParams2);
   return 1;
  },
  filterOperators: getRightOperators(),
  renderCell: (params: GridRenderCellParams<any>) =>
   renderCell
    ? renderCell(params)
    : xRenderCell(params, col.field, structure?.Type || 'string'), // why?
 };
};

export const renderColWithStructure = (
 columns: GridColDef[],
 structure: StructureType[],
) => {
 let output = [] as GridColDef[];
 if (!structure.length) {
  throw new Error('renderColWithStructure Structure is empty');
 }
 if (!Array.isArray(columns)) {
  return [];
 }
 columns?.forEach((col) => {
  if (col.field === 'actions') return;
  const structureObj = findStructure(structure, col.field);

  if (!structureObj) return;

  const colName = getTranslation(structure, col.field);

  if (isWeightModelField(structureObj)) {
   output.push(
    renderColObj(
     col,
     colName,
     structureObj,
     (params: GridRenderCellParams<any>) => {
      const customValue = resolveObjPathDots(params.row, `${col.field}.Weight`);
      return xRenderCell(params, colName, 'double', String(customValue ?? ''));
     },
     (value: GridValueGetter) => resolveObjPathDots(value, col.field),
    ) as GridColDef,
   );
  } else if (structureObj.Type === 'model') {
   const nameVar = isJson(structureObj.ForeignKeyDefaultFilter || '')
    ? JSON.parse(structureObj.ForeignKeyDefaultFilter || '').fields.split(
       ',',
      )[1]
    : 'Name';

   const colType =
    structureObj?.Type === 'model'
     ? structureObj?.InnerStructure?.find((item) => item.Name === nameVar)?.Type
     : structureObj?.Type;

   output.push(
    renderColObj(
     col,
     colName,
     structureObj,
     (params: GridRenderCellParams<any>) => {
      const customValue = resolveObjPathDots(
       params.row,
       `${col.field}.${nameVar}`,
      );
      return xRenderCell(
       params,
       colName,
       colType || 'string',
       String(customValue),
      );
     },
     (value: GridValueGetter) => resolveObjPathDots(value, col.field),
    ) as GridColDef,
   );
  } else if (structureObj.Type === 'array') {
   output.push(renderColObj(col, colName, structureObj) as GridColDef);
  } else if (structureObj.Type === 'enum') {
   output.push(
    renderColObj(
     col,
     colName,
     structureObj,
     (params: GridRenderCellParams<any>) =>
      xRenderCell(
       params,
       colName,
       structureObj.Type,
       structureObj.EnumValues[params.value],
      ),
    ) as GridColDef,
   );
  } else {
   output.push(renderColObj(col, colName, structureObj) as GridColDef);
  }
 });
 //  console.log(output);
 return output;
};

